import { Page, TextField, Modal, Select } from '../components/forms';
import { useEffect, useState, useCallback } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Banner,
  EmptyState,
  Spinner,
  FormLayout,
  Icon,
  Divider,
} from '@shopify/polaris';
import { PlusIcon, DeleteIcon, EditIcon, SearchIcon } from '@shopify/polaris-icons';
import { useApi } from '../hooks/useApi';

interface AlternativeGroup {
  id: number;
  name: string;
  description: string | null;
  allocation_strategy: 'priority' | 'fifo' | 'lowest_stock';
  is_active: number;
  member_count: number;
  members?: GroupMember[];
  created_at: string;
}

interface GroupMember {
  id: number;
  product_id: number;
  priority: number;
  notes: string | null;
  sku: string;
  title: string;
  barcode: string | null;
  stock_quantity: number;
  available_quantity: number;
}

interface ProductSearchResult {
  id: number;
  sku: string;
  title: string;
  barcode: string | null;
  stock_quantity: number;
  available_quantity: number;
  existing_group_name: string | null;
}

export default function AlternativeProducts() {
  const { fetchApi, error } = useApi();
  const [groups, setGroups] = useState<AlternativeGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<AlternativeGroup | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Form states
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupStrategy, setNewGroupStrategy] = useState<string>('priority');

  // Product search
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<ProductSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const loadGroups = useCallback(async () => {
    const data = await fetchApi<{ groups: AlternativeGroup[] }>('/warehouse/alternatives?include_members=true');
    if (data) {
      setGroups(data.groups || []);
    }
    setInitialLoad(false);
  }, [fetchApi]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;

    const result = await fetchApi<{ success: boolean; group_id: number }>('/warehouse/alternatives', {
      method: 'POST',
      body: JSON.stringify({
        name: newGroupName,
        description: newGroupDescription || null,
        allocation_strategy: newGroupStrategy,
      }),
    });

    if (result?.success) {
      setCreateModalOpen(false);
      setNewGroupName('');
      setNewGroupDescription('');
      setNewGroupStrategy('priority');
      loadGroups();
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (!confirm('Are you sure you want to delete this alternative group?')) return;

    const result = await fetchApi<{ success: boolean }>(`/warehouse/alternatives/${groupId}`, {
      method: 'DELETE',
    });

    if (result?.success) {
      setSelectedGroup(null);
      loadGroups();
    }
  };

  const handleSearchProducts = async (query: string) => {
    setProductSearchQuery(query);
    if (query.length < 2) {
      setProductSearchResults([]);
      return;
    }

    setSearchLoading(true);
    const data = await fetchApi<{ products: ProductSearchResult[] }>(
      `/warehouse/alternatives/search/products?q=${encodeURIComponent(query)}${selectedGroup ? `&exclude_group=${selectedGroup.id}` : ''}`
    );
    if (data) {
      setProductSearchResults(data.products || []);
    }
    setSearchLoading(false);
  };

  const handleAddProductToGroup = async (productId: number) => {
    if (!selectedGroup) return;

    const result = await fetchApi<{ success: boolean }>(`/warehouse/alternatives/${selectedGroup.id}/members`, {
      method: 'POST',
      body: JSON.stringify({ product_id: productId }),
    });

    if (result?.success) {
      setProductSearchQuery('');
      setProductSearchResults([]);
      loadGroups();
      // Refresh selected group
      const updatedGroups = await fetchApi<{ groups: AlternativeGroup[] }>('/warehouse/alternatives?include_members=true');
      if (updatedGroups) {
        const updated = updatedGroups.groups.find(g => g.id === selectedGroup.id);
        if (updated) setSelectedGroup(updated);
      }
    }
  };

  const handleRemoveProductFromGroup = async (productId: number) => {
    if (!selectedGroup) return;
    if (!confirm('Remove this product from the alternative group?')) return;

    const result = await fetchApi<{ success: boolean }>(`/warehouse/alternatives/${selectedGroup.id}/members/${productId}`, {
      method: 'DELETE',
    });

    if (result?.success) {
      loadGroups();
      // Refresh selected group
      const updatedGroups = await fetchApi<{ groups: AlternativeGroup[] }>('/warehouse/alternatives?include_members=true');
      if (updatedGroups) {
        const updated = updatedGroups.groups.find(g => g.id === selectedGroup.id);
        if (updated) setSelectedGroup(updated);
      }
    }
  };

  const handleUpdateGroup = async () => {
    if (!selectedGroup || !newGroupName.trim()) return;

    const result = await fetchApi<{ success: boolean }>(`/warehouse/alternatives/${selectedGroup.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: newGroupName,
        description: newGroupDescription || null,
        allocation_strategy: newGroupStrategy,
      }),
    });

    if (result?.success) {
      setEditModalOpen(false);
      loadGroups();
      setSelectedGroup(prev => prev ? {
        ...prev,
        name: newGroupName,
        description: newGroupDescription || null,
        allocation_strategy: newGroupStrategy as any,
      } : null);
    }
  };

  const openEditModal = (group: AlternativeGroup) => {
    setNewGroupName(group.name);
    setNewGroupDescription(group.description || '');
    setNewGroupStrategy(group.allocation_strategy);
    setEditModalOpen(true);
  };

  const strategyOptions = [
    { label: 'Priority (use lowest priority number first)', value: 'priority' },
    { label: 'Lowest Stock (use product with most stock first)', value: 'lowest_stock' },
    { label: 'FIFO (first in, first out)', value: 'fifo' },
  ];

  const getStrategyBadge = (strategy: string) => {
    switch (strategy) {
      case 'priority': return <Badge tone="info">Priority</Badge>;
      case 'lowest_stock': return <Badge tone="warning">Lowest Stock</Badge>;
      case 'fifo': return <Badge>FIFO</Badge>;
      default: return <Badge>{strategy}</Badge>;
    }
  };

  if (initialLoad) {
    return (
      <Page title="Alternative Products">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Alternative Products"
      subtitle="Manage interchangeable products for flexible order fulfillment"
      primaryAction={{
        content: 'Create Group',
        icon: PlusIcon,
        onAction: () => setCreateModalOpen(true),
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Alternative Groups</Text>

              {groups.length === 0 ? (
                <EmptyState
                  heading="No alternative groups yet"
                  image=""
                >
                  <p>Create groups of interchangeable products to enable flexible fulfillment.</p>
                </EmptyState>
              ) : (
                <BlockStack gap="200">
                  {groups.map(group => (
                    <div
                      key={group.id}
                      onClick={() => setSelectedGroup(group)}
                      style={{
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: selectedGroup?.id === group.id ? '#f0f0f0' : 'transparent',
                        border: selectedGroup?.id === group.id ? '1px solid #ccc' : '1px solid transparent',
                      }}
                    >
                      <BlockStack gap="100">
                        <InlineStack align="space-between">
                          <Text variant="bodyMd" fontWeight="semibold" as="span">{group.name}</Text>
                          <Badge>{`${group.member_count} products`}</Badge>
                        </InlineStack>
                        <InlineStack gap="200">
                          {getStrategyBadge(group.allocation_strategy)}
                          {!group.is_active && <Badge tone="critical">Inactive</Badge>}
                        </InlineStack>
                      </BlockStack>
                    </div>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          {selectedGroup ? (
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <BlockStack gap="100">
                    <Text variant="headingLg" as="h2">{selectedGroup.name}</Text>
                    {selectedGroup.description && (
                      <Text variant="bodyMd" tone="subdued" as="p">{selectedGroup.description}</Text>
                    )}
                  </BlockStack>
                  <InlineStack gap="200">
                    <Button icon={EditIcon} onClick={() => openEditModal(selectedGroup)}>Edit</Button>
                    <Button icon={DeleteIcon} tone="critical" onClick={() => handleDeleteGroup(selectedGroup.id)}>Delete</Button>
                  </InlineStack>
                </InlineStack>

                <Divider />

                <InlineStack gap="400">
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued" as="span">Allocation Strategy</Text>
                    {getStrategyBadge(selectedGroup.allocation_strategy)}
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued" as="span">Total Combined Stock</Text>
                    <Text variant="headingMd" as="span">
                      {selectedGroup.members?.reduce((sum, m) => sum + (m.available_quantity || 0), 0) || 0} available
                    </Text>
                  </BlockStack>
                </InlineStack>

                <Divider />

                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text variant="headingMd" as="h3">Products in Group</Text>
                    <Button icon={PlusIcon} onClick={() => setAddProductModalOpen(true)}>Add Product</Button>
                  </InlineStack>

                  {selectedGroup.members && selectedGroup.members.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'text']}
                      headings={['Priority', 'SKU', 'Title', 'Stock', 'Available', 'Actions']}
                      rows={selectedGroup.members.map(member => [
                        <Badge key={`priority-${member.product_id}`}>{String(member.priority)}</Badge>,
                        member.sku,
                        member.title,
                        String(member.stock_quantity || 0),
                        String(member.available_quantity || 0),
                        <Button
                          key={`remove-${member.product_id}`}
                          icon={DeleteIcon}
                          tone="critical"
                          variant="plain"
                          onClick={() => handleRemoveProductFromGroup(member.product_id)}
                        />,
                      ])}
                    />
                  ) : (
                    <Banner tone="info">
                      No products in this group yet. Add products to enable alternative fulfillment.
                    </Banner>
                  )}
                </BlockStack>
              </BlockStack>
            </Card>
          ) : (
            <Card>
              <EmptyState
                heading="Select a group"
                image=""
              >
                <p>Select an alternative group from the list to view and manage its products.</p>
              </EmptyState>
            </Card>
          )}
        </Layout.Section>
      </Layout>

      {/* Create Group Modal */}
      <Modal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create Alternative Group"
        primaryAction={{
          icon: PlusIcon, content: 'Create',
          onAction: handleCreateGroup,
          disabled: !newGroupName.trim(),
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setCreateModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Group Name"
              requiredIndicator value={newGroupName}
              onChange={setNewGroupName}
              autoComplete="off"
              placeholder="e.g., Blue Widget Variants"
            />
            <TextField
              label="Description (optional)"
              value={newGroupDescription}
              onChange={setNewGroupDescription}
              autoComplete="off"
              placeholder="e.g., Same product from different suppliers"
              multiline={2}
            />
            <Select
              label="Allocation Strategy"
              options={strategyOptions}
              value={newGroupStrategy}
              onChange={setNewGroupStrategy}
              helpText="How should the system choose which product to allocate when multiple are available?"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Edit Group Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Alternative Group"
        primaryAction={{
          content: 'Save',
          onAction: handleUpdateGroup,
          disabled: !newGroupName.trim(),
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setEditModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Group Name"
              requiredIndicator value={newGroupName}
              onChange={setNewGroupName}
              autoComplete="off"
            />
            <TextField
              label="Description (optional)"
              value={newGroupDescription}
              onChange={setNewGroupDescription}
              autoComplete="off"
              multiline={2}
            />
            <Select
              label="Allocation Strategy"
              options={strategyOptions}
              value={newGroupStrategy}
              onChange={setNewGroupStrategy}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Add Product Modal */}
      <Modal
        open={addProductModalOpen}
        onClose={() => {
          setAddProductModalOpen(false);
          setProductSearchQuery('');
          setProductSearchResults([]);
        }}
        title="Add Product to Group"
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Search products"
              value={productSearchQuery}
              onChange={handleSearchProducts}
              autoComplete="off"
              placeholder="Search by SKU, title, or barcode..."
              prefix={<Icon source={SearchIcon} />}
            />

            {searchLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
                <Spinner size="small" />
              </div>
            )}

            {productSearchResults.length > 0 && (
              <BlockStack gap="200">
                {productSearchResults.map(product => (
                  <div
                    key={product.id}
                    style={{
                      padding: '12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                    }}
                  >
                    <InlineStack align="space-between">
                      <BlockStack gap="100">
                        <Text variant="bodyMd" fontWeight="semibold" as="span">{product.sku}</Text>
                        <Text variant="bodySm" as="span">{product.title}</Text>
                        <InlineStack gap="200">
                          <Badge tone="info">{`${product.available_quantity} available`}</Badge>
                          {product.existing_group_name && (
                            <Badge tone="warning">{`In: ${product.existing_group_name}`}</Badge>
                          )}
                        </InlineStack>
                      </BlockStack>
                      <Button icon={PlusIcon}
                        onClick={() => handleAddProductToGroup(product.id)}
                        disabled={!!product.existing_group_name}
                      >
                        Add
                      </Button>
                    </InlineStack>
                  </div>
                ))}
              </BlockStack>
            )}

            {productSearchQuery.length >= 2 && !searchLoading && productSearchResults.length === 0 && (
              <Text variant="bodySm" tone="subdued" as="p">No products found matching your search.</Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
